import { LionInputTelDropdown, getFlagSymbol } from '@lion/ui/input-tel-dropdown.js'
import { BaseComponent } from './base-component.js'
import { html, css, TemplateResult, CSSResult, CSSResultArray, PropertyValues } from 'lit'
import { customElement, property } from 'lit/decorators.js'
import { caratDown } from './images/icons/carat-down.js'
import { alert as alertIcon } from './images/icons/alert.js'
import type { RegionCode } from '@lion/ui/types/input-tel.js'
import { Required, Validator } from '@lion/ui/form-core.js'

type FeedbackConditionType = 'error' | 'warning' | 'success' | 'info'
type FeedbackConditionMeta = {
  dirty: boolean
  el: HTMLElement
  filled: boolean
  focused: boolean
  modelValue: string
  prefilled: boolean
  submitted: boolean
  touched: boolean
}

/**
 * Wrapper component for lion-input-tel-dropdown
 *
 * Props:
 * - error-message: string
 * - full-width: boolean
 * - label: string
 * - placeholder: string
 * - allow-empty: boolean
 * - initial-value: string (the initial value of the input)
 * - onChange: (value: string) => void
 * - onValidate: (valid: boolean) => void
 *
 * LION INPUT API DOCS
 * https://lion-web.netlify.app/components/input-tel-dropdown/api-table/
 */
@customElement('il-input-tel')
export default class InputTelComponent extends LionInputTelDropdown {
  @property({ type: String, attribute: 'initial-value' }) initialValue?: string
  @property({ type: String, attribute: 'error-message' }) errorMessage?: string
  @property({ type: Boolean, attribute: 'force-error', reflect: true }) forceError?: boolean = false
  @property({ type: Boolean, attribute: 'full-width', reflect: true }) fullWidth?: boolean = false
  @property({ type: Boolean, attribute: 'allow-empty' }) allowEmpty?: boolean = false
  @property({ type: Object }) onChange?: (value: string) => void
  @property({ type: Object }) onValidate?: (valid: boolean) => void

  // override the default validators to include Required if not allowEmpty
  override validators: Validator[] = [
    ...this.validators,
    ...(this.allowEmpty ? [] : [new Required()])
  ]

  private get isValid(): boolean {
    return !this.hasFeedbackFor?.includes('error')
  }

  private get selectedFlag(): string {
    return this.activeRegion ? getFlagSymbol(this.activeRegion) : ''
  }

  protected override firstUpdated(_changedProperties: PropertyValues): void {
    super.firstUpdated(_changedProperties)

    // If modelValue is empty string, set it to undefined
    // This is because lion-input will add a dial code to empty strings by default
    // and we want to show either an empty input or a placeholder
    this.modelValue = this.initialValue || undefined
  }

  // override the internal method that Lion calls when model value changes
  protected override _onModelValueChanged({ modelValue }: { modelValue: string }) {
    super._onModelValueChanged({ modelValue })

    new Promise(resolve => setTimeout(resolve, 1)).then(() => {
      this.onChange?.(modelValue ?? '')
      this.onValidate?.(this.isValid)
    })
  }

  /**
   * Override the default feedback conditions:
   * - Don't show error feedback if the input is not dirty
   * - Otherwise, show feedback as normal
   */
  override _showFeedbackConditionFor(type: FeedbackConditionType, meta: FeedbackConditionMeta): boolean {
    if (type === 'error' && !meta.dirty) {
      return false
    }

    return super._showFeedbackConditionFor(type, meta)
  }

  /**
   * overrides the lion-input prefix template
   * Used for the flag select menu
   */
  protected override _inputGroupPrefixTemplate(): TemplateResult {
    return html`
      <div class="il-input-tel__select-wrapper" slot="prefix" data-testid="il-input-tel__select-wrapper">
        <div class="il-input-tel__select__trigger" data-testid="il-input-tel__select__trigger">
          <span data-testid="il-input-tel__select__flag">${this.selectedFlag}</span>
          ${caratDown}
        </div>
        <select class="il-input-tel__select" @change="${this.onRegionSelected}" data-testid="il-input-tel__select">
          ${this._allowedOrAllRegions?.map(regionCode => this.regionOptionTemplate(regionCode))}
        </select>
      </div>
    `
  }

  /**
   * overrides the lion-input feedback template
   * Used for the error message
   */
  protected override _feedbackTemplate(): TemplateResult {
    if (!this.errorMessage) return html``

    return html`
      <div class="il-input-tel__error-message" data-testid="il-input-tel__error-message">
        ${alertIcon}
        ${this.errorMessage}
      </div>
    `
  }

  private regionOptionTemplate(regionCode: RegionCode): TemplateResult {
    const dialCode = this.getDialCodeForRegion(regionCode)
    const flag = getFlagSymbol(regionCode)

    return html`
      <option value="${regionCode}" ?selected="${regionCode === this.activeRegion}">
        ${flag} ${regionCode} ${dialCode ? `(+${dialCode})` : ''}
      </option>
    `
  }

  private getDialCodeForRegion(regionCode: RegionCode): string {
    return this._phoneUtil.getCountryCodeForRegionCode(regionCode) ?? ''
  }

  private setDialCodeForRegion(regionCode: RegionCode): void {
    const newDialCode = this.getDialCodeForRegion(regionCode)
    this.modelValue = `+${newDialCode}`
    this.resetInteractionState()
  }

  private onRegionSelected = (e: Event): void => {
    const regionCode = (e.target as HTMLSelectElement).value as RegionCode

    this._setActiveRegion(regionCode)
    this.setDialCodeForRegion(regionCode)
  }

  static override styles: (CSSResult | CSSResultArray)[] = [
    BaseComponent.styles as CSSResult,
    css`
      :host([full-width]) {
        width: 100%;
      }

      :host {
        --input-tel-border-color: var(--il-color-neutrals-200);
        --input-tel-bg-color: var(--il-color-neutrals-50);
        --input-tel-height: 44px; /* match il-input */

        display: flex;
        flex-direction: column;
        gap: 6px;
      }

      .input-group__container {
        display: flex;
      }
      .input-group__input {
        width: 100%;
      }
      .form-field__group-two {
        display: flex;
        flex-direction: column;
        gap: 6px;
      }

      ::slotted([slot='label']) {
        color: var(--il-color-neutrals-300);
        font-size: var(--il-font-size-14);
        line-height: var(--il-font-line-height-140);
        letter-spacing: var(--il-font-letter-spacing-2);
      }

      /*
       * Cannot target w/ sufficient specificity, light DOM enforces overrides.
       * Need to use !important
       */
      ::slotted([slot='input']){
        width: 100% !important;
        height: var(--input-tel-height) !important;
        padding: 12px !important;

        background-color: var(--input-tel-bg-color) !important;

        border-radius: 0px 6px 6px 0px !important;
        border: 1px solid var(--input-tel-border-color) !important;
        border-left: none !important;
        outline: none !important;

        font-size: var(--il-font-size-16) !important;
        font-weight: var(--input-tel-font-weight) !important;
        line-height: var(--il-font-line-height-125) !important;
        letter-spacing: var(--il-font-letter-spacing-2) !important;
        color: var(--il-color-neutrals-350) !important;
      }
      ::slotted([slot='input'])::placeholder {
        color: var(--il-color-neutrals-300) !important;
      }

      .il-input-tel__select-wrapper {
        position: relative;
        display: flex;
        align-items: center;
        height: var(--input-tel-height);

        border-radius: 6px 0px 0px 6px;
        border: 1px solid var(--input-tel-border-color);
        border-right: 1px solid var(--il-color-neutrals-200);
        background-color: var(--il-color-neutrals-0);

        padding: 12px 16px !important;

        font-size: var(--il-font-size-16);
        font-weight: var(--input-tel-font-weight);
        line-height: var(--il-font-line-height-125);
        letter-spacing: var(--il-font-letter-spacing-2);
      }

      .il-input-tel__select__trigger {
        display: flex;
        align-items: center;
        gap: 8px;

        font-size: 1.2rem;
        pointer-events: none;
        padding: 0 2px;
      }

      .il-input-tel__select {
        position: absolute;
        inset: 0;
        opacity: 0;
        cursor: pointer;
        outline: none;
      }

      .il-input-tel__label {
        color: var(--il-color-neutrals-300);
        font-size: var(--il-font-size-14);
        line-height: var(--il-font-line-height-140);
        letter-spacing: var(--il-font-letter-spacing-2);
      }

      .il-input-tel__error-message {
        display: none;
        color: var(--il-color-warning);
        font-size: var(--il-font-size-14);
        line-height: var(--il-font-line-height-140);
        letter-spacing: var(--il-font-letter-spacing-2);
      }
      :host([shows-feedback-for~="error"]) .il-input-tel__error-message,
      :host([force-error]) .il-input-tel__error-message {
        display: flex;
        align-items: center;
        gap: 8px;
      }

      /* focus / hover / error / disabled states */
      :host:hover {
        --input-tel-border-color: var(--il-color-neutrals-250);
      }
      :host(:focus-within) {
        --input-tel-border-color: var(--il-color-accent-200);
        --input-tel-bg-color: var(--il-color-neutrals-0);
      }
      :host([shows-feedback-for~="error"]),
      :host([force-error]) {
        --input-tel-border-color: var(--il-color-warning) !important;
      }
      :host([disabled]) {
        opacity: 0.5;
        pointer-events: none;
      }
    `,
  ] as (CSSResult | CSSResultArray)[]
}
